import _ from 'lodash';
import React, { ReactElement, useEffect } from 'react';
import { Button, Spin, Table, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import EstablishmentFeeFormatEnum from '@/Api/Deal/EstablishmentFeeFormatEnum';
import IDeal from '@/Api/Deal/IDeal';
import IDealQuote from '@/Api/Deal/IDealQuote';
import IDealQuoteProperty from '@/Api/Deal/IDealQuoteProperty';
import MortgageTypeEnum from '@/Api/Deal/MortgageTypeEnum';
import {
    dealGetAction,
    dealQuotesListAction,
} from '@/Lead/actions';
import {
    currentDealAutoQuoteSelector,
    dealSelector,
} from '@/Lead/selectors';
import { IDictionary } from '@/utilities/IDictionary';
import dayjs from 'dayjs';
import { IGlobalState } from '@/reducer';
import IToken from '@/Auth/IToken';
import {
    currencyFormatter,
    percentageFormatter,
} from '@/utilities/formatters';
import CentredLayout from './CentredLayout';
import { decodedTokenSelector } from '@/Auth/selectors';

interface ITableRow {
    data?: string| ReactElement[];
    description: string;
}

interface ITableColumn {
    dataIndex: string;
    key: string;
}
const mortgageTypeLabels: IDictionary<string> = {
    [MortgageTypeEnum.FirstMortgage]: 'First Mortgage',
    [MortgageTypeEnum.SecondMortgage]: 'Second Mortgage',
};

export default function Quote(): ReactElement {
    const dispatch: Dispatch = useDispatch();
    const deal: IDeal = useSelector(dealSelector);
    const token: IToken = useSelector(decodedTokenSelector);
    const quote: IDealQuote = useSelector((state: IGlobalState) => currentDealAutoQuoteSelector(state, token.dealUuid));

    useEffect(() => {
        if (!deal || !deal.uuid) {
            dispatch(dealGetAction());
        }
    }, [
        deal,
        dispatch,
    ]);

    useEffect(() => {
        if (!quote && token.dealUuid) {
            dispatch(dealQuotesListAction(token.dealUuid));
        }
    }, [
        dispatch,
        quote,
        token.dealUuid,
    ]);

    if (!deal || !quote) {
        return (
            <CentredLayout pageName='quote'>
                <h3 className='headline'>Your Loan Quote</h3>
                <Spin indicator={<LoadingOutlined spin={true} />} />
            </CentredLayout>
        );
    }

    const propertiesBlock: JSX.Element[] = _.map(quote.properties, (property: IDealQuoteProperty) => <div>{property.name}</div>);

    const columns: ITableColumn[] = [
        {
            dataIndex: 'description',
            key: 'description',
        },
        {
            dataIndex: 'data',
            key: 'data',
        },
    ];

    const loanDetailsData: ITableRow[] = [
        { description: 'Quote Number', data: quote.code },
        { description: 'Application Number', data: deal.code },
        { description: 'Date', data: dayjs(quote.createdTime).format('D MMMM YYYY') },
        { description: 'Loan Amount', data: currencyFormatter.format(quote.loanAmount) },
        { description: 'Loan To Value Ratio', data: `${percentageFormatter.format(quote.lvr / 100) } of the property value (or less)` },
        { description: 'Security', data: mortgageTypeLabels[quote.mortgageType] },
        { description: 'Term', data: `${quote.termMonths } month term` },
        { description: 'Interest Rate', data: `${percentageFormatter.format(quote.interestRate / 100) } per annum` },
        {
            description: 'Establishment Fee',
            data: quote.establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage ? percentageFormatter.format(quote.establishmentFeePercentage / 100) : currencyFormatter.format(quote.establishmentFee),
        },
        { description: 'Legal Fee (Estimate)', data: `${currencyFormatter.format(quote.legalFees) } Legal fee + GST/Outlays (approximate only)` },
        { description: 'Property', data: propertiesBlock },
    ];

    const disbursalData: ITableRow[] = [
        { description: 'Total Loan Amount', data: currencyFormatter.format(quote.loanAmount) },
        { description: 'Less' },
        {
            description: `${quote.termMonths } Months Interest at ${ percentageFormatter.format(quote.interestRate / 100)}`,
            data: currencyFormatter.format(quote.loanAmount * (quote.interestRate / 100) / 12 * quote.termMonths),
        },
        { description: 'Establishment Fee', data: currencyFormatter.format(quote.establishmentFee) },
        { description: 'Legal Fee (Estimate)', data: `${currencyFormatter.format(quote.legalFees) } inc. GST` },
        { description: 'Estimated Outlays and Registration Fees', data: currencyFormatter.format(quote.estimatedOutlays) },
        { description: 'Current Property Debt', data: currencyFormatter.format(quote.currentDebt) },
        { description: 'Balance (approx.)', data: currencyFormatter.format(quote.netBalance) },
    ];

    return (
        <CentredLayout pageName='quote'>
            <h3 className='headline'>Your Loan Quote</h3>
            <p className='intro'>
                Thanks for your enquiry with Funding - the home of a faster, simpler and reliable mortgage solution.
            </p>
            <Typography.Title className='details-subheading' level={4}>Loan Details</Typography.Title>
            <Table
                bordered={true}
                className='quote-details'
                columns={columns}
                dataSource={loanDetailsData}
                pagination={false}
                showHeader={false}
            />
            <Typography.Title className='disbursed-subheading' level={4}>Disbursed as follows</Typography.Title>
            <Table
                bordered={true}
                className='quote-details'
                columns={columns}
                dataSource={disbursalData}
                pagination={false}
                showHeader={false}
            />
            <p className='fees'>All loan setup fees are paid on successful settlement of the loan.</p>
            <p className='upfront-cost'>The only upfront cost to kick start your loan is {currencyFormatter.format(quote.commitmentFee)} to cover the valuation of the security property / properties.</p>
            <p className='proceed'>If you wish to proceed with us, please click the below to start your 5 minute online application.</p>
            <div className='actions'>
                <Link className='apply-now' to={`${process.env.PUBLIC_FRONTEND_HOST}/quote/${quote.linkCode}`}>
                    <Button type='primary'>Start Application</Button>
                </Link>
            </div>
            <p className='questions'>Any questions please do not hesitate to call or email the team. We look forward to receiving your completed loan application.</p>
            <p className='tos'>The above quote is subject to review once the application is received. Feel free to review our <a className='link' href='https://www.funding.com.au/credit-guide'>credit guide here</a>.</p>
            <p className='tmd'>View our <a className='link' href='https://www.funding.com.au/tmd-credit'>target market determination (TMD)</a>. It sets out the class of consumers for whom the product, including its key attributes, would likely be consistent with their likely objectives, financial situation and needs.</p>
            <p className='thanks'>Thank you for your quote request.</p>
        </CentredLayout>
    );
}

